<template>
  <div class="draws-card">
    <h3 class="draws-title">Top Jogadores com Mais Empates</h3>
    <p v-if="statsReferenceText" class="stats-reference-text">({{ statsReferenceText }})</p>
    <div class="draws-list-container">
      <div v-if="loading" class="loading-spinner">Carregando...</div> 
      <div v-else-if="error" class="error-message">{{ error }}</div>
      <div v-else-if="!hasData" class="draws-player-empty">Nenhum dado de empate encontrado.</div>
      <div v-else>
        <template v-for="periodKey in orderedPeriods" :key="periodKey">
          <template v-if="hasPlayersInPeriod(periodKey)">
            <h4 class="period-title">{{ getPeriodDisplayName(periodKey) }}</h4>
            <div v-for="(player, index) in periodsData[periodKey]" :key="player.player_name" class="draws-player-row">
              <div class="player-info">
                <span class="player-position">{{ index + 1 }}.</span>
                <span class="draws-player-name">{{ player.player_name }}</span>
              </div>
              <div class="player-stats">
                <div class="stat-item">
                  <span class="stat-label">Partidas:</span>
                  <span class="stat-value">{{ player.total_matches_last_10h }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Empates:</span>
                  <span class="stat-value">{{ player.draws_last_10h }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Sequência sem empatar:</span>
                  <span class="stat-value">{{ player.draw_streak_last_10h }}</span>
                </div>
              </div>
            </div>
          </template>
          <template v-else-if="periodKey && periodsData[periodKey] && periodsData[periodKey].length === 0">
            <p class="period-empty-message" style="color: #aaa; font-style: italic;">Nenhum jogador encontrado para o período: {{ getPeriodDisplayName(periodKey) }}.</p>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import apiService from '@/services/api.js';

// Initialize with empty arrays
const periodsData = ref({
  madrugada: [],
  tarde: [],
  noite: []
});
const statsReferenceText = ref('');
const loading = ref(true);
const error = ref('');
let intervalId = null;

// Function to determine the order of periods based on current time
const getPeriodsOrder = () => {
  const currentHour = new Date().getHours();
  
  // 02:00 - 10:59: madrugada first
  if (currentHour >= 2 && currentHour < 11) {
    return ['madrugada', 'noite', 'tarde'];
  }
  // 11:00 - 17:59: tarde first
  else if (currentHour >= 11 && currentHour < 18) {
    return ['tarde', 'madrugada', 'noite'];
  }
  // 18:00 - 01:59: noite first
  else {
    return ['noite', 'tarde', 'madrugada'];
  }
};

// Computed properties for better reactivity
const orderedPeriods = computed(() => getPeriodsOrder());

// Helper function to check if a period has players
const hasPlayersInPeriod = (periodKey) => {
  return periodsData.value && 
         periodsData.value[periodKey] && 
         Array.isArray(periodsData.value[periodKey]) && 
         periodsData.value[periodKey].length > 0;
};

const hasData = computed(() => {
  if (!periodsData.value) return false;
  return orderedPeriods.value.some(key => hasPlayersInPeriod(key));
});

const getPeriodDisplayName = (periodKey) => {
  const names = {
    madrugada: 'Madrugada',
    tarde: 'Tarde',
    noite: 'Noite',
  };
  return names[periodKey] || periodKey.charAt(0).toUpperCase() + periodKey.slice(1);
};

const getStatsReferenceDisplay = (refKey) => {
  if (refKey === 'last_10_hours') {
    return 'Dados das últimas 10 horas';
  }
  return refKey ? `Dados de ${refKey.replace('_', ' ')}` : '';
};

async function fetchTopDraws() {
  loading.value = true;
  error.value = '';
  try {
    // Simplified API call
    const response = await apiService.get('/players-and-teams/top-draws/');
    
    // Handle different response formats
    let responseData;
    
    if (response && typeof response.json === 'function') {
      // If response is a fetch Response object
      responseData = await response.json();
    } else if (response && response.data) {
      // If response is an axios-like response
      responseData = response.data;
    } else {
      // If response is already the data
      responseData = response;
    }
    
    // Always ensure periodsData has the expected structure
    if (responseData) {
      periodsData.value = {
        madrugada: Array.isArray(responseData.madrugada) ? responseData.madrugada : [],
        tarde: Array.isArray(responseData.tarde) ? responseData.tarde : [],
        noite: Array.isArray(responseData.noite) ? responseData.noite : [],
      };
      statsReferenceText.value = getStatsReferenceDisplay(responseData.stats_reference_period);
    } else {
      // Reset to empty arrays if no data
      periodsData.value = {
        madrugada: [],
        tarde: [],
        noite: []
      };
      statsReferenceText.value = '';
    }
  } catch (err) {
    console.error('Erro ao buscar dados de empates:', err);
    error.value = 'Falha ao carregar dados. Tente novamente mais tarde.';
    periodsData.value = {}; 
  } finally {
    loading.value = false;
  }
}



onMounted(() => {
  fetchTopDraws();
  intervalId = setInterval(fetchTopDraws, 600000); 
});

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId);
  }
});

</script>

<style scoped>
.draws-card {
  background-color: #2c2c2c; 
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  color: #f0f0f0; 
  display: flex;
  flex-direction: column;
  max-height: 150vh; 
}

.draws-title {
  text-align: center;
  color: var(--color-text); 
  margin-bottom: 0.25rem;
  font-size: 1.1rem;
}

.stats-reference-text {
  text-align: center;
  font-size: 0.8rem;
  color: #a0a0a0;
  margin-bottom: 0.75rem;
}

.draws-list-container {
  overflow-y: auto;
  flex-grow: 1;
  padding-right: 5px; 
}

.period-title {
  color: #e0e0e0;
  font-size: 1rem;
  font-weight: bold;
  margin-top: 0.8rem;
  margin-bottom: 0.4rem;
  padding-bottom: 0.2rem;
  border-bottom: 1px solid #444;
}

.draws-player-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.7rem 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: background-color 0.2s ease;
  border-radius: 6px;
  margin-bottom: 0.3rem;
}

.draws-player-row:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.draws-player-row:last-child {
  border-bottom: none;
}

.player-info {
  display: flex;
  align-items: center;
  margin-bottom: 0.4rem;
  width: 100%;
}

.draws-player-name {
  font-weight: 600;
  color: #e0e0e0;
  margin-left: 0.3rem;
  font-size: 0.95rem;
}

.player-position {
  color: #1abc9c;
  font-weight: bold;
  margin-right: 0.5rem;
  font-size: 0.95rem;
}

.player-stats {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding-left: 1.5rem;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.stat-label {
  color: #999;
  font-size: 0.8rem;
}

.stat-value {
  color: #1abc9c;
  font-weight: 600;
  font-size: 0.85rem;
}

.draws-player-empty,
.loading-spinner,
.error-message {
  text-align: center;
  padding: 1rem;
  color: #a0a0a0;
  font-style: italic;
}

.draws-list-container::-webkit-scrollbar {
  width: 8px;
}

.draws-list-container::-webkit-scrollbar-track {
  background: transparent; /* Transparent by default */
  border-radius: 4px;
}

.draws-list-container::-webkit-scrollbar-thumb {
  background-color: transparent; /* Transparent by default */
  border-radius: 4px;
}

/* Show scrollbar on hover */
.draws-list-container:hover::-webkit-scrollbar-track {
  background: #383838; /* Original track color */
}

.draws-list-container:hover::-webkit-scrollbar-thumb {
  background-color: #1abc9c; /* Original thumb color */
}

.draws-list-container:hover::-webkit-scrollbar-thumb:hover {
  background-color: #05846b; /* Original thumb hover color */
}
</style>
